import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";
import { useAccount } from "wagmi";
import { useConnectModal, useAccountModal, useChainModal } from "@rainbow-me/rainbowkit";
import { LuMenu, LuX, LuChartBar, LuWallet, LuGift, LuShieldCheck, LuTrophy } from "react-icons/lu";
import { NavbarItem } from "./navbar/NavbarItem";
import { useNavItems } from "./navbar/useNavItems";
import { useActiveAddress } from "../futurex/hooks/useActiveAddress";
import { useIsAdmin } from "../futurex/hooks/useIsAdmin";
import { shortAddress } from "../futurex/lib/format";
import { FUTUREX_CHAIN } from "../futurex/config";

const logoMark = "/usdx-icon.png";

const WALLET_GRADIENT = "linear-gradient(to right, #041A3B 0%, #10479B 100%)";

const WALLET_PILL_STYLE = {
    height: "40px",
    minHeight: "40px",
    minWidth: "unset",
    padding: "0 20px",
    borderRadius: "9999px",
    background: WALLET_GRADIENT,
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
};

const WALLET_DETAILS_STYLE = {
    minWidth: "unset",
    padding: "4px 16px 4px 5px",
    borderRadius: "9999px",
    background: WALLET_GRADIENT,
    color: "#FFFFFF",
    border: "none",
};

/* In-app routes for FutureX — shown as a slim tab strip below the main bar */
const APP_LINKS = [
    { label: "Markets", to: "/", icon: LuChartBar, exact: true },
    { label: "Portfolio", to: "/portfolio", icon: LuWallet },
    { label: "Leaderboard", to: "/leaderboard", icon: LuTrophy },
    { label: "Refer & Earn", to: "/affiliate", icon: LuGift },
];

function AppTabLink({ to, icon: Icon, label, exact, onClick }) {
    return (
        <NavLink
            to={to}
            end={exact}
            onClick={onClick}
            className={({ isActive }) =>
                [
                    "flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium font-['IBM_Plex_Sans'] transition-colors duration-150",
                    isActive
                        ? "text-usdx-accent bg-usdx-accent/10"
                        : "text-usdx-ink-muted hover:text-usdx-ink hover:bg-usdx-surface-sunken",
                ].join(" ")
            }
        >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            {label}
        </NavLink>
    );
}

/**
 * Connect / account pill backed by the RainbowKit modals: "Connect" opens the wallet list, a connected wallet shows its
 * address and opens the account modal (copy, disconnect), and a wallet on another network gets a switch button.
 */
function WalletButton() {
    const { address, isConnected, chainId } = useAccount();
    const { openConnectModal } = useConnectModal();
    const { openAccountModal } = useAccountModal();
    const { openChainModal } = useChainModal();

    if (!isConnected || !address) {
        return (
            <button type="button" onClick={() => openConnectModal?.()} style={WALLET_PILL_STYLE}>
                Connect
            </button>
        );
    }
    if (chainId !== FUTUREX_CHAIN.id) {
        return (
            <button type="button" onClick={() => openChainModal?.()} style={{ ...WALLET_PILL_STYLE, background: "#B42318" }}>
                Wrong network
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={() => openAccountModal?.()}
            style={WALLET_DETAILS_STYLE}
            className="flex items-center gap-2 text-sm font-semibold"
            aria-label="Wallet account"
        >
            <LuWallet className="h-4 w-4" />
            <span className="num">{shortAddress(address)}</span>
        </button>
    );
}

const Header = () => {
    const navItems = useNavItems();
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const address = useActiveAddress();
    const { isAnyAdmin } = useIsAdmin();


    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 1200) setIsMobileOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const closeMobile = () => setIsMobileOpen(false);

    return (
        <header className="sticky top-0 z-40 w-full header-3d">
            {/* ── Top bar: brand · ecosystem nav · wallet ── */}
            <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
                {/* Brand */}
                <Link to="/" aria-label="USDX Smart" className="flex shrink-0 items-center gap-2">
                    <img className="h-8 w-8" src={logoMark} alt="" aria-hidden="true" />
                    <span className="flex flex-col justify-center leading-none">
                        <span
                            style={{ color: "#0d8ce0" }}
                            className="text-lg font-extrabold tracking-tight"
                        >
                            USDX
                        </span>
                        <span
                            style={{ color: "#2b9be8" }}
                            aria-hidden="true"
                            className="mt-0.5 flex w-full justify-between text-[8px] font-semibold uppercase"
                        >
                            {["S", "M", "A", "R", "T"].map((letter) => (
                                <span key={letter}>{letter}</span>
                            ))}
                        </span>
                    </span>
                </Link>

                {/* Ecosystem nav — desktop only */}
                <nav className="hidden flex-1 items-center justify-center gap-1 nav:flex">
                    {navItems?.map((item) => (
                        <NavbarItem key={item?.label} item={item} />
                    ))}
                </nav>

                <div className="flex shrink-0 items-center gap-2">
                    <WalletButton />
                    <ThemeToggle />
                    <button
                        type="button"
                        aria-label="Toggle navigation menu"
                        aria-expanded={isMobileOpen}
                        onClick={() => setIsMobileOpen(!isMobileOpen)}
                        className="rounded-md p-2 text-usdx-ink-muted transition-colors duration-150 hover:bg-usdx-surface-sunken hover:text-usdx-ink nav:hidden"
                    >
                        {isMobileOpen ? (
                            <LuX className="h-5 w-5" />
                        ) : (
                            <LuMenu className="h-5 w-5" />
                        )}
                    </button>
                </div>
            </div>

            {/* ── App tab strip: Markets · Portfolio · Refer & Earn [· Admin] ── */}
            <div className="border-t border-usdx-line/60">
                <div className="mx-auto flex h-10 max-w-7xl items-center gap-1 overflow-x-auto whitespace-nowrap px-4 sm:px-6 lg:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {APP_LINKS?.map((link) => (
                        <AppTabLink key={link?.to} {...link} />
                    ))}
                    {/* Admin tab — only shown to role holders */}
                    {address && isAnyAdmin && (
                        <AppTabLink
                            to="/admin"
                            icon={LuShieldCheck}
                            label="Admin"
                        />
                    )}
                </div>
            </div>

            {/* ── Mobile dropdown ── */}
            <div
                className={`overflow-hidden bg-usdx-surface transition-[max-height] duration-300 ease-in-out nav:hidden ${
                    isMobileOpen ? "max-h-[32rem] border-t border-usdx-line" : "max-h-0"
                }`}
            >
                {/* Ecosystem links */}
                <nav className="space-y-1 px-4 py-3 sm:px-6">
                    {navItems?.map((item) => (
                        <NavbarItem
                            key={item?.label}
                            item={item}
                            variant="mobile"
                            onClick={closeMobile}
                        />
                    ))}
                </nav>
            </div>
        </header>
    );
};

export default Header;
